
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  Add,
  Remove,
  KeyboardArrowLeft,
  ShoppingCartOutlined,
  CheckCircleOutline,
  LocalOfferOutlined,
  PictureAsPdfOutlined,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";

const CATEGORY_TABS = {
  "Соларни панели": 1,
  "Фасади": 2,
  "Индустриални": 3,
};

const COLORS = {
  primary: "#0D3B66",
  secondary: "#0061A6",
  accent: "#F4D35E",
  background: "#F5F8FC",
  light: "#EEF4F9",
  border: "#DFE7F0",
  text: "#273649",
  muted: "#64748B",
  white: "#FFFFFF",
  success: "#18865B",
};

const formatPrice = (value) =>
  new Intl.NumberFormat("bg-BG", {
    style: "currency",
    currency: "EUR",
  }).format(value);

function publicPath(path) {
  if (!path || typeof path !== "string") return "";

  let result = path.trim().replace(/\\/g, "/");

  // Convert paths stored relative to the JSON directory
  // into paths accessible from the public website.
  while (result.startsWith("../")) {
    result = result.slice(3);
  }

  result = result.replace(/^\.\/+/, "");
  result = result.replace(/^\/+/, "");

  return `/${result}`;
}

function ProductSection({ title, number, children }) {
  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        border: `1px solid ${COLORS.border}`,
        borderRadius: 3,
        bgcolor: COLORS.white,
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Typography
          variant="overline"
          sx={{
            color: COLORS.secondary,
            fontWeight: 800,
            letterSpacing: 1,
          }}
        >
          {number}
        </Typography>

        <Typography
          variant="h6"
          sx={{
            mt: 0.5,
            mb: 1.25,
            color: COLORS.primary,
            fontWeight: 700,
          }}
        >
          {title}
        </Typography>

        {children}
      </CardContent>
    </Card>
  );
}

export default function AgentInfo() {
  const params = useParams();
  const navigate = useNavigate();
  const { dispatch } = useCart();

  const isMobile = useMediaQuery("(max-width:600px)");
  const isDesktop = useMediaQuery("(min-width:1100px)");

  // Supports both the old /agentInfo/:abb links
  // and the new /agentInfo/:id links.
  const productParam = params.id ?? params.abb ?? "";

  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedPack, setSelectedPack] = useState("");
  const [qty, setQty] = useState(1);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    let cancelled = false;

    async function loadProduct() {
      setLoading(true);
      setLoadError(false);
      setAgent(null);
      setSelectedPack("");
      setQty(1);

      try {
        const response = await fetch("/db/agents.json");

        if (!response.ok) {
          throw new Error("Неуспешно зареждане на каталога.");
        }

        const data = await response.json();

        if (!Array.isArray(data)) {
          throw new Error("Невалиден формат на продуктовия каталог.");
        }

        // Prefer the unique ID. Fall back to the legacy ABB
        // so existing bookmarks can continue to work.
        const found =
          data.find(
            (item) => String(item.id) === String(productParam)
          ) ||
          data.find(
            (item) =>
              String(item.abb ?? "").trim().toLowerCase() ===
              String(productParam).trim().toLowerCase()
          );

        if (cancelled) return;

        if (!found) {
          setAgent(null);
          return;
        }

        setAgent(found);

        if (Array.isArray(found.pack) && found.pack.length > 0) {
          setSelectedPack(found.pack[0]);
        }
      } catch (error) {
        console.error("Грешка при зареждане на продукта:", error);

        if (!cancelled) {
          setLoadError(true);
          setAgent(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadProduct();

    return () => {
      cancelled = true;
    };
  }, [productParam]);

  const packs = Array.isArray(agent?.pack) ? agent.pack : [];

  const selectedIndex = packs.indexOf(selectedPack);

  const selectedPrice =
    selectedIndex >= 0
      ? Number(agent?.priceNumberEuro?.[selectedIndex])
      : NaN;

  const imageSrc = publicPath(agent?.image);
  const instructionsHref = publicPath(agent?.instructions);

  const highlights = useMemo(
    () =>
      Array.isArray(agent?.highlights)
        ? agent.highlights.filter(
            (item) => typeof item === "string" && item.trim()
          )
        : [],
    [agent]
  );

  const infoSections = useMemo(() => {
    if (!agent?.info || typeof agent.info !== "object") {
      return [];
    }

    return [
      {
        title: "Как работи?",
        content: agent.info.intro,
        number: "01",
      },
      {
        title: "Предимства",
        content: agent.info.benefits,
        number: "02",
      },
      {
        title: "Приложение",
        content: agent.info.apply,
        number: "03",
      },
    ].filter(
      (section) =>
        typeof section.content === "string" &&
        section.content.trim().length > 0
    );
  }, [agent]);

  useEffect(() => {
    if (!agent) return;

    const productName = agent.nameBulgarian || agent.name || "Продукт";

    document.title = `${productName} | TC Energy`;

    let descriptionMeta = document.querySelector(
      'meta[name="description"]'
    );

    const createdMeta = !descriptionMeta;

    if (!descriptionMeta) {
      descriptionMeta = document.createElement("meta");
      descriptionMeta.setAttribute("name", "description");
      document.head.appendChild(descriptionMeta);
    }

    const description =
      typeof agent.info?.intro === "string" && agent.info.intro.trim()
        ? agent.info.intro.trim().replace(/\s+/g, " ").slice(0, 155)
        : `${productName} – професионален препарат от каталога на TC Energy. Вижте наличните опаковки и цени.`;

    descriptionMeta.setAttribute("content", description);

    return () => {
      document.title = "TC Energy";

      // Keep an existing site's description meta tag in place.
      // Remove only the tag created by this component.
      if (createdMeta) {
        descriptionMeta?.remove();
      }
    };
  }, [agent]);

  const showMessage = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const backToAgents = () => {
    navigate("/preparati-za-solarni-paneli", {
      state: {
        selectedTab: CATEGORY_TABS[agent?.category] ?? 0,
      },
    });
  };

  const addToCart = () => {
    if (!agent || !selectedPack || selectedIndex < 0) {
      showMessage("Моля, избери опаковка.", "warning");
      return;
    }

    if (!Number.isFinite(selectedPrice) || selectedPrice <= 0) {
      showMessage("Няма валидна цена за избраната опаковка.", "error");
      return;
    }

    if (!Number.isInteger(qty) || qty < 1 || qty > 10000) {
      showMessage("Невалидно количество.", "warning");
      return;
    }

    dispatch({
      type: "ADD",
      item: {
        id: `${agent.id}-${selectedPack}`,
        productId: agent.id,
        name: agent.nameBulgarian || agent.name,
        pack: selectedPack,
        qty,
        priceEUR: selectedPrice,
        image: imageSrc,
      },
    });

    showMessage("Продуктът е добавен в количката.");
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: 300,
          display: "grid",
          placeItems: "center",
          px: 2,
        }}
      >
        <Typography color="text.secondary">
          Зареждане на продукта...
        </Typography>
      </Box>
    );
  }

  if (loadError || !agent) {
    return (
      <Box
        sx={{
          maxWidth: 700,
          mx: "auto",
          px: 2,
          py: 8,
          textAlign: "center",
        }}
      >
        <Typography variant="h6" color={COLORS.primary}>
          {loadError
            ? "Възникна проблем при зареждането на продукта."
            : "Не са намерени данни за продукта."}
        </Typography>

        <Button
          variant="outlined"
          onClick={() => navigate("/preparati-za-solarni-paneli")}
          sx={{ mt: 2, textTransform: "none" }}
        >
          Към всички препарати
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100%",
        bgcolor: COLORS.background,
        pb: { xs: 4, md: 7 },
      }}
    >
      <Box
        sx={{
          maxWidth: 1240,
          mx: "auto",
          px: { xs: 1.5, sm: 3, md: 4 },
        }}
      >
        <Button
          startIcon={<KeyboardArrowLeft />}
          onClick={backToAgents}
          sx={{
            mt: { xs: 1.5, md: 3 },
            mb: { xs: 1.5, md: 2.5 },
            ml: -0.75,
            color: COLORS.muted,
            textTransform: "none",
            "&:hover": {
              color: COLORS.primary,
              bgcolor: "#EAF0F6",
            },
          }}
        >
          Всички препарати
        </Button>

        {/* Main product area */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: isDesktop
              ? "minmax(0, 0.9fr) minmax(0, 1.1fr)"
              : "minmax(0, 1fr)",
            gap: { xs: 2, md: 3 },
            alignItems: "start",
          }}
        >
          {/* Product image */}
          <Card
            elevation={0}
            sx={{
              minWidth: 0,
              overflow: "hidden",
              border: `1px solid ${COLORS.border}`,
              borderRadius: 3,
              bgcolor: COLORS.white,
            }}
          >
            <Box
              sx={{
                minHeight: { xs: 240, sm: 300, md: 430 },
                p: { xs: 2, sm: 3 },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                background:
                  "radial-gradient(circle at 50% 40%, #FFFFFF 0%, #F0F5FA 100%)",
              }}
            >
              {imageSrc ? (
                <CardMedia
                  component="img"
                  image={imageSrc}
                  alt={agent.alt || agent.nameBulgarian || agent.name}
                  sx={{
                    width: "100%",
                    maxWidth: 420,
                    maxHeight: { xs: 260, sm: 320, md: 380 },
                    objectFit: "contain",
                  }}
                />
              ) : (
                <Typography color="text.secondary">
                  Няма налична снимка
                </Typography>
              )}

              <Chip
                icon={
                  <CheckCircleOutline
                    sx={{ color: `${COLORS.success} !important` }}
                  />
                }
                label="Професионален продукт"
                size="small"
                sx={{
                  mt: 2,
                  bgcolor: "#EAF7F0",
                  color: "#176B4A",
                  fontWeight: 600,
                }}
              />
            </Box>
          </Card>

          {/* Product details and purchase */}
          <Card
            elevation={0}
            sx={{
              minWidth: 0,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 3,
              bgcolor: COLORS.white,
            }}
          >
            <CardContent
              sx={{
                p: { xs: 2, sm: 3 },
                "&:last-child": { pb: { xs: 2, sm: 3 } },
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: COLORS.secondary,
                  fontWeight: 800,
                  letterSpacing: 1.1,
                }}
              >
                {agent.category || "ПРОДУКТ"}
              </Typography>

              <Typography
                component="h1"
                variant="h4"
                sx={{
                  mt: 0.5,
                  color: COLORS.primary,
                  fontWeight: 800,
                  fontSize: {
                    xs: "1.55rem",
                    sm: "1.9rem",
                    md: "2.1rem",
                  },
                  lineHeight: 1.2,
                  overflowWrap: "anywhere",
                }}
              >
                {agent.nameBulgarian || agent.name}
              </Typography>

              {agent.name && agent.nameBulgarian && (
                <Typography
                  variant="subtitle1"
                  sx={{
                    mt: 0.75,
                    color: COLORS.muted,
                    overflowWrap: "anywhere",
                  }}
                >
                  {agent.name}
                </Typography>
              )}

              <Divider sx={{ my: 2.5, borderColor: COLORS.border }} />

              {/* Summary */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "minmax(0, 1fr)",
                    sm: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    p: 1.75,
                    borderRadius: 2,
                    bgcolor: COLORS.light,
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: COLORS.muted,
                      mb: 0.5,
                      fontWeight: 700,
                    }}
                  >
                    РАЗРЕЖДАНЕ
                  </Typography>

                  <Typography
                    sx={{
                      color: COLORS.text,
                      fontWeight: 700,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {agent.dilution || "Според инструкциите"}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 1.75,
                    borderRadius: 2,
                    bgcolor: COLORS.light,
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: COLORS.muted,
                      mb: 0.5,
                      fontWeight: 700,
                    }}
                  >
                    ОПАКОВКИ
                  </Typography>

                  <Typography
                    sx={{
                      color: COLORS.text,
                      fontWeight: 700,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {packs.length ? packs.join(" · ") : "Няма налични данни"}
                  </Typography>
                </Box>
              </Box>

              {/* Highlights are optional */}
              {highlights.length > 0 && (
                <Box sx={{ mt: 2.5 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ color: COLORS.primary, fontWeight: 800, mb: 1 }}
                  >
                    Основни предимства
                  </Typography>

                  <Box sx={{ display: "grid", gap: 1 }}>
                    {highlights.map((highlight, index) => (
                      <Box
                        key={`${index}-${highlight}`}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                        }}
                      >
                        <CheckCircleOutline
                          sx={{
                            color: COLORS.success,
                            fontSize: 20,
                            mt: "2px",
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: COLORS.text,
                            lineHeight: 1.7,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {highlight}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 2.5, borderColor: COLORS.border }} />

              {/* Pack selector */}
              <Typography
                variant="h6"
                sx={{
                  color: COLORS.primary,
                  fontWeight: 800,
                  fontSize: "1.1rem",
                  mb: 0.5,
                }}
              >
                Избери опаковка
              </Typography>

              <Typography
                variant="body2"
                sx={{ color: COLORS.muted, mb: 1.5 }}
              >
                Избери желания размер, за да видиш цената.
              </Typography>

              {packs.length > 0 ? (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(2, minmax(0, 1fr))",
                      sm: "repeat(auto-fit, minmax(125px, 1fr))",
                    },
                    gap: 1.25,
                  }}
                >
                  {packs.map((pack, index) => {
                    const price = Number(agent.priceNumberEuro?.[index]);
                    const validPrice =
                      Number.isFinite(price) && price > 0;
                    const selected = selectedPack === pack;

                    return (
                      <Paper
                        key={`${agent.id}-${pack}`}
                        component="button"
                        type="button"
                        aria-pressed={selected}
                        onClick={() => setSelectedPack(pack)}
                        elevation={0}
                        sx={{
                          position: "relative",
                          minWidth: 0,
                          p: 1.5,
                          textAlign: "left",
                          font: "inherit",
                          cursor: "pointer",
                          border: `1.5px solid ${
                            selected ? COLORS.secondary : COLORS.border
                          }`,
                          borderRadius: 2,
                          bgcolor: selected ? "#EFF6FF" : COLORS.white,
                          color: COLORS.text,
                          transition: "border-color 160ms ease",
                          "&:hover": {
                            borderColor: COLORS.secondary,
                          },
                          "&:focus-visible": {
                            outline: `2px solid ${COLORS.secondary}`,
                            outlineOffset: 2,
                          },
                        }}
                      >
                        {selected && (
                          <CheckCircleOutline
                            sx={{
                              position: "absolute",
                              top: 7,
                              right: 7,
                              fontSize: 18,
                              color: COLORS.secondary,
                            }}
                          />
                        )}

                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 800,
                            pr: 2.5,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {pack}
                        </Typography>

                        <Typography
                          sx={{
                            mt: 1,
                            color: selected
                              ? COLORS.secondary
                              : COLORS.primary,
                            fontSize: "1.05rem",
                            fontWeight: 800,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {validPrice ? formatPrice(price) : "Цена при запитване"}
                        </Typography>
                      </Paper>
                    );
                  })}
                </Box>
              ) : (
                <Alert severity="info">
                  За този продукт няма въведени опаковки.
                </Alert>
              )}

              <Divider sx={{ my: 2.5, borderColor: COLORS.border }} />

              {/* Quantity */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: COLORS.primary, fontWeight: 800 }}
                  >
                    Количество
                  </Typography>
                  <Typography variant="caption" sx={{ color: COLORS.muted }}>
                    Брой опаковки
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 2,
                    bgcolor: COLORS.white,
                    p: 0.5,
                  }}
                >
                  <IconButton
                    size="small"
                    aria-label="Намали количеството"
                    disabled={qty <= 1}
                    onClick={() =>
                      setQty((current) => Math.max(1, current - 1))
                    }
                  >
                    <Remove fontSize="small" />
                  </IconButton>

                  <Typography
                    sx={{
                      minWidth: 38,
                      textAlign: "center",
                      fontWeight: 800,
                    }}
                  >
                    {qty}
                  </Typography>

                  <IconButton
                    size="small"
                    aria-label="Увеличи количеството"
                    disabled={qty >= 10000}
                    onClick={() =>
                      setQty((current) => Math.min(10000, current + 1))
                    }
                  >
                    <Add fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              {/* Total */}
              <Box
                sx={{
                  mt: 2.5,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "#F0F6FC",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1.5,
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ color: COLORS.muted }}>
                    Обща цена
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: COLORS.muted }}
                  >
                    {selectedPack || "Не е избрана опаковка"} · {qty} бр.
                  </Typography>
                </Box>

                <Typography
                  variant="h5"
                  sx={{
                    color: COLORS.primary,
                    fontWeight: 900,
                    fontSize: { xs: "1.3rem", sm: "1.55rem" },
                  }}
                >
                  {Number.isFinite(selectedPrice) && selectedPrice > 0
                    ? formatPrice(selectedPrice * qty)
                    : "—"}
                </Typography>
              </Box>

              <Button
                variant="contained"
                fullWidth
                disableElevation
                startIcon={<ShoppingCartOutlined />}
                onClick={addToCart}
                disabled={
                  !selectedPack ||
                  selectedIndex < 0 ||
                  !Number.isFinite(selectedPrice) ||
                  selectedPrice <= 0
                }
                sx={{
                  mt: 2,
                  py: 1.6,
                  borderRadius: 2,
                  bgcolor: COLORS.secondary,
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 800,
                  "&:hover": { bgcolor: COLORS.primary },
                }}
              >
                Добави в количката
              </Button>

              <Typography
                variant="caption"
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 0.75,
                  mt: 1.5,
                  color: COLORS.muted,
                  textAlign: "center",
                }}
              >
                <LocalOfferOutlined sx={{ fontSize: 16 }} />
                Посочените цени са с включен ДДС.
              </Typography>

              {/* Optional instructions PDF */}
              {instructionsHref && (
                <Button
                  component="a"
                  href={instructionsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  fullWidth
                  startIcon={<PictureAsPdfOutlined />}
                  sx={{
                    mt: 1.5,
                    py: 1.25,
                    borderRadius: 2,
                    borderColor: COLORS.border,
                    color: COLORS.primary,
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                >
                  Техническа информация / Инструкции
                </Button>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Product descriptions */}
        {infoSections.length > 0 && (
          <Box sx={{ mt: { xs: 3, md: 4 } }}>
            <Typography
              component="h2"
              variant="h5"
              sx={{
                mb: 2,
                color: COLORS.primary,
                fontWeight: 800,
                fontSize: { xs: "1.3rem", sm: "1.6rem" },
              }}
            >
              Повече за продукта
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(3, minmax(0, 1fr))",
                },
                gap: 2,
              }}
            >
              {infoSections.map((section) => (
                <ProductSection
                  key={section.title}
                  title={section.title}
                  number={section.number}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: COLORS.text,
                      lineHeight: 1.85,
                      whiteSpace: "pre-line",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {section.content}
                  </Typography>
                </ProductSection>
              ))}
            </Box>
          </Box>
        )}

        {/* Optional highlights fallback for products without info */}
        {highlights.length > 0 && infoSections.length === 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography
              component="h2"
              variant="h5"
              sx={{ mb: 2, color: COLORS.primary, fontWeight: 800 }}
            >
              Информация за продукта
            </Typography>
            <ProductSection title="Основни предимства" number="01">
              <Box component="ul" sx={{ pl: 2.5, my: 0 }}>
                {highlights.map((item, index) => (
                  <Typography
                    component="li"
                    variant="body2"
                    key={`${index}-${item}`}
                    sx={{ mb: 1, lineHeight: 1.8 }}
                  >
                    {item}
                  </Typography>
                ))}
              </Box>
            </ProductSection>
          </Box>
        )}

        {/* Certifications */}
        <Box
          sx={{
            mt: 3,
            p: { xs: 2, sm: 2.5 },
            border: `1px solid ${COLORS.border}`,
            borderRadius: 3,
            bgcolor: COLORS.white,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: { xs: 2, sm: 3 },
            flexWrap: "wrap",
          }}
        >
          {["bio.png", "drops.jpg", "tuv-sud.png"].map((file) => (
            <Box
              key={file}
              sx={{
                width: { xs: 52, sm: 64 },
                height: { xs: 52, sm: 64 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CardMedia
                component="img"
                image={`/images/${file}`}
                alt={file === "tuv-sud.png" ? "TÜV SÜD" : file}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            </Box>
          ))}

          <Typography
            variant="body2"
            sx={{
              flex: { xs: "1 1 100%", sm: "1 1 260px" },
              textAlign: { xs: "center", sm: "left" },
              color: COLORS.muted,
              lineHeight: 1.7,
            }}
          >
            Информация за сертификати и характеристики според данните за
            конкретния продукт.
          </Typography>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2800}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: isMobile ? "center" : "left",
        }}
      >
        <Alert
          onClose={() =>
            setSnackbar((current) => ({ ...current, open: false }))
          }
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
